import { useState, useEffect } from 'react';
import { SeoEntry, SeoFormData, useSeoCreate, useSeoUpdate } from '../../hooks/useSeoOperations';
import Swal from 'sweetalert2';
import { Modal } from '../ui/modal';

interface SeoEditModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    seo?: SeoEntry | null;
}

const ENTITY_TYPE_LABELS: Record<string, string> = {
    product: '🛒 Product',
    category: '📂 Category',
    brand: '🏷️ Brand',
    seo: '📄 Static Page',
};

export default function SeoEditModal({ isOpen, onClose, onSuccess, seo }: SeoEditModalProps) {
    const [formData, setFormData] = useState<SeoFormData>({
        url: '',
        meta_title: '',
        meta_keyword: '',
        meta_description: '',
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const { createSeo, loading: createLoading, error: createError } = useSeoCreate();
    const { updateSeo, loading: updateLoading, error: updateError } = useSeoUpdate();

    const isEditing = !!seo?.seo_id;

    useEffect(() => {
        if (seo) {
            setFormData({
                url: seo.url,
                meta_title: seo.meta_title || '',
                meta_keyword: '',
                meta_description: seo.meta_description || '',
                entity_type: seo.entity_type,
                entity_id: seo.entity_id,
            });
        } else {
            setFormData({
                url: '',
                meta_title: '',
                meta_keyword: '',
                meta_description: '',
            });
        }
        setErrors({});
    }, [seo, isOpen]);

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.meta_title.trim()) {
            newErrors.meta_title = 'Meta Title is required';
        } else if (formData.meta_title.length > 60) {
            newErrors.meta_title = 'Meta Title should not exceed 60 characters';
        }

        if (!formData.meta_description.trim()) {
            newErrors.meta_description = 'Meta Description is required';
        } else if (formData.meta_description.length > 160) {
            newErrors.meta_description = 'Meta Description should not exceed 160 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        try {
            if (isEditing && seo?.seo_id) {
                const result = await updateSeo(seo.seo_id, formData);
                if (result) {
                    onSuccess();
                    onClose();
                    Swal.fire({ toast: true, position: 'top-end', icon: 'success', text: 'SEO record updated successfully', showConfirmButton: false, timer: 2000, timerProgressBar: true });
                }
            } else {
                const result = await createSeo(formData);
                if (result) {
                    onSuccess();
                    onClose();
                    Swal.fire({ toast: true, position: 'top-end', icon: 'success', text: 'SEO record created successfully', showConfirmButton: false, timer: 2000, timerProgressBar: true });
                }
            }
        } catch (error) {
            console.error('Error submitting form:', error);
        }
    };

    if (!isOpen) return null;

    const loading = createLoading || updateLoading;
    const serverError = createError || updateError;

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="max-w-2xl" showCloseButton={false}>
            <div className="flex flex-col max-h-[90vh] bg-white dark:bg-gray-900 rounded-3xl overflow-hidden shadow-2xl">
                {/* Fixed Header */}
                <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-white dark:bg-gray-900">
                    <div>
                        <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                            {isEditing ? 'Edit SEO' : 'Create SEO'}
                        </h3>
                        {seo && (
                            <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs px-2 py-0.5 rounded-full bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400 font-medium">
                                    {ENTITY_TYPE_LABELS[seo.entity_type] ?? seo.entity_type}
                                </span>
                                <span className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">{seo.name}</span>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* URL Preview Banner */}
                {seo?.url && (
                    <div className="px-6 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Page URL</p>
                        <code className="text-sm font-mono text-brand-600 dark:text-brand-400">{seo.url}</code>
                    </div>
                )}

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    <form id="seo-form" onSubmit={handleSubmit} className="space-y-6">
                        {serverError && (
                            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 text-red-700 dark:text-red-400">
                                <p className="text-sm">{serverError}</p>
                            </div>
                        )}

                        {/* Meta Title */}
                        <div>
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                                Meta Title <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.meta_title}
                                onChange={(e) => {
                                    setFormData(prev => ({ ...prev, meta_title: e.target.value }));
                                    if (errors.meta_title) setErrors({ ...errors, meta_title: '' });
                                }}
                                placeholder="Page title for search engines (max 60 chars)"
                                maxLength={60}
                                className={`w-full px-4 py-2.5 rounded-xl border transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 dark:bg-gray-800 dark:text-white ${errors.meta_title ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'}`}
                            />
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{formData.meta_title.length}/60 characters</p>
                            {errors.meta_title && <p className="mt-1 text-sm text-red-500">{errors.meta_title}</p>}
                        </div>

                        {/* Meta Keywords */}
                        <div>
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                                Meta Keywords <span className="text-gray-400 font-normal">(optional)</span>
                            </label>
                            <textarea
                                value={formData.meta_keyword}
                                onChange={(e) => setFormData(prev => ({ ...prev, meta_keyword: e.target.value }))}
                                placeholder="Comma-separated keywords"
                                rows={2}
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 dark:bg-gray-800 dark:text-white"
                            />
                        </div>

                        {/* Meta Description */}
                        <div>
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                                Meta Description <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                value={formData.meta_description}
                                onChange={(e) => {
                                    setFormData(prev => ({ ...prev, meta_description: e.target.value }));
                                    if (errors.meta_description) setErrors({ ...errors, meta_description: '' });
                                }}
                                placeholder="Page description for search engines (max 160 chars)"
                                maxLength={160}
                                rows={3}
                                className={`w-full px-4 py-2.5 rounded-xl border transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 dark:bg-gray-800 dark:text-white ${errors.meta_description ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'}`}
                            />
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{formData.meta_description.length}/160 characters</p>
                            {errors.meta_description && <p className="mt-1 text-sm text-red-500">{errors.meta_description}</p>}
                        </div>
                    </form>
                </div>

                {/* Fixed Footer */}
                <div className="px-6 py-5 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-3 bg-gray-50/50 dark:bg-gray-800/30">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        className="px-6 py-2.5 text-sm font-bold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="seo-form"
                        disabled={loading}
                        className="px-6 py-2.5 text-sm font-bold text-white bg-brand-500 border border-brand-500 rounded-xl hover:bg-brand-600 shadow-[0_4px_12px_rgba(59,130,246,0.3)] transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2"
                    >
                        {loading ? (
                            <>
                                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                Processing...
                            </>
                        ) : (
                            isEditing ? 'Update SEO' : 'Create SEO'
                        )}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
